import type { ProductionTypes } from "@/types/CubeDto"

const strings = {
  platform: {
    name: 'eFriends Map'
  },
  errors: {
    EMAIL_SUBSCRIBE_NO_VALID_EMAIL_ADDRESS: "Keine gültige E-Mail gesetzt",
    CANNOT_CHANGE_NICKNAME: "Nickname konnte nicht geändert werden",
    NICKNAME_ALREADY_USED: "Nickname existiert bereits!",
    INVALID_NICKNAME: "Ungültiger Nickname",
  },
  global: {
    title: import.meta.env.VITE_APP_NAME,
    save: 'Speichern',
    cancel: 'abbrechen',
    loading: 'Loading ...',
    updateApp:
      'Eine neue Version der eFriends App ist verfügbar und wird in wenigen Sekunden geladen.',
    back: 'zurück',
    next: 'Weiter',
    changePassword: 'Passwort ändern',
    logout: 'Abmelden',
    edit: 'Bearbeiten',
    remove: 'Entfernen',
    yes: 'Ja',
    no: 'Nein',
    ok: 'Ok',
    today: 'Heute',
    notifications: 'Push-Benachrichtigungen',
    emailNotifications: 'E-Mail-Benachrichtigungen',
    emailNotificationsError: 'E-Mail-Benachrichtigungen konnten nicht aktiviert werden (@error)',
    toggleMenuSlidein: 'Menü ein-/ausblenden',
    toggleFriendsSlidein: 'Liste ein-/ausblenden',
    cubeNotReachableMessage: `Wir können deinen Cube im Moment nicht erreichen.
      Bitte stelle sicher, dass die Internetverbindung funktioniert.`,
    communitylessCubeHint: "Wenn du eine eFriends WATCH+ hast, dann wird hier dein aktueller Verbrauch oder die Einspeisung angezeigt.",
    communitylessHistoryHintConsumption: {
      day: "Wenn du eine eFriends WATCH+ hast, dann wird hier dein Verbrauch bzw. die Einspeisung über den Tagesverlauf angezeigt.",
      month: "Wenn du eine eFriends WATCH+ hast, dann wird hier dein Verbrauch bzw. die Einspeisung über den Monatsverlauf angezeigt."
    },
    communitylessHistoryHintPrice: "In der unten angezeigten Kurve siehst du deinen aktuellen Strompreis",
    communitylessFriendsHintLong: `
        Die Möglichkeit sich mit anderen zu verbinden und Strom zu beziehen wird aktiviert sobald du in der eFriends Community aktiv bist.
        <br /><br />
        Gerne kannst du aber schon jetzt einen Blick in die Karte werfen wer in deiner Umgebung aktiv ist.`,
    communitylessDevicesHint: "Diese Funktion ist verfügbar, sobald du eine eFriends WATCH+ aktiv verwendest.",
    meterNotReachableMessage: `Deine Echtzeitmessung ist nicht erreichbar.
      Deine Bilanz wird von einem Prognosewert abgeleitet.`,
    close: 'Schließen',
    from: 'Ab',
    meterlessNoteText: 'Dein Verbrauch wird von einem Prognosewert abgeleitet.',
    meterUnreachableNoteText:
      'Deine Bilanz wird von einem Prognosewert abgeleitet weil dein Zähler nicht erreichbar ist.',
    nickNameEdit: 'Nickname ändern',
    myDataSavedSuccessfully: "Die Daten wurden gespeichert",
  },

  authentication: {
    provideUserNameAndPassword: 'Bitte geben Sie Benutzername und Passwort an.',
    passwordResetMailSent: `Wir haben ein E-Mail mit einen Link zum Zurücksetzen des Passworts gesendet. Es kann ein paar Minuten dauern, bis du das E-Mail erhältst.
      Falls du kein E-Mail erhalten solltest, versuche es bitte erneut.`,
    resetPasswordTitle: 'Passwort zurücksetzen',
    usernamePlaceholder: 'Benutzername/E-Mail',
  },
  efriends: {
    menuTitle: import.meta.env.VITE_FRIENDS_MENU_TITLE,
    title: import.meta.env.VITE_FRIENDS_TITLE,
    mapTitle: 'Karte',
    mapSubtitle: import.meta.env.VITE_FRIENDS_SUBTITLE,
    listTitle: 'Liste',
    donateesTitle: 'Ich spende oder tausche',
    contributorsTitle: 'Ich erhalte Spenden von',
    eegConnectionsTitle: 'Erneuerbare Energie Gemeinschaften',
    participationFactorCommunity: 'Teilnahmefaktor Community',
    participationFactorEEG: 'Teilnahmefaktor EEG',
    participationFactor: 'Teilnahmefaktor',
    communityConnectionsTitle: 'eFriends Community',
    producersTitle: 'Ich beziehe von (Bruttopreise)',
    consumersTitle: 'Ich liefere an (Nettopreise)',
    doConnectButton: 'Strom beziehen',
    dontConnectButton: 'Keinen Strom beziehen',
    removeConnectionButton: 'Verbindung trennen',
    doDonateButton: 'Strom spenden',
    stopDonatingButton: 'Nicht mehr spenden',

    mapsPopup: {
      connectionTextBidirectional: 'Du beziehst von und lieferst an @cube',
      connectionTextProducer: 'Du beziehst von @cube',
      connectionTextConsumer: 'Du lieferst an @cube',

      connectionTextDonate: 'Du spendest an @cube',
      connectionTextDonateAndConsume: 'Du spendest an und beziehst von @cube',

      connectionTextContributes: '@cube spendet an dich',
      connectionTextContributesAndConsume: '@cube spendet an dich und du beziehst',
      productionDataLabel: 'Produktionsdaten',
      twoHourAvg: '2-Stunden-Durchschnitt:',
      surplusYesterdayLabel: 'Einspeisung gestern:',
      surplusLastMonthLabel: 'Einspeisung letztes Monat:',
      productionDetailsLink: 'Produktionsdetails »',
      energylinesLabel: 'Energylines',
      energylinesUsed: 'verwendet',
      energylinesNeeded: 'benötigt',
      energylinesLow: 'Du hast nur noch @count freie @unit, und kannst von dieser Anlage noch keinen Strom beziehen.',
      energylinesNone: 'Du hast keine freien Energylines mehr und kannst von keinen zusätzlichen Anlagen Strom beziehen.',
      energylines: {
        communitylessHint: "Wenn du Teil der eFriends Community bist, erhältst du Energylines und kannst du dich mit anderen verbinden."
      },
      householdsCanConnect: '@count Haushalt(e) können sich noch verbinden',
      comingSoon: 'Demnächst verfügbar!',
      allConnectionsTaken: 'Alle verfügbaren Verbindungen zu dieser Anlage sind bereits belegt.',
      likedBy: '@count @unit gefällt das.',
    },
    currentSellingPrice: 'Basisverkaufspreis aktuell',
    currentPrice: 'Preis / kWh',
    priceFromEEG: 'Verrechnung durch EEG',
    nextPriceLabel: 'Neuer Preis',
    residueConsumption: 'Restenergiebezug von',
    residueProduction: 'Restenergie Einlieferung an',
    showMore: 'Mehr anzeigen',
    hasFreeCapacity: 'Du kannst dich mit @cube verbinden.',
    sort: 'Sortieren',
    sorting: {
      lowestPriceFirst: 'Günstige zuerst',
      highestPriceFirst: 'Teure zuerst',
      shortestDistanceFirst: 'Nahe zuerst',
      longestDistanceFirst: 'Entfernte zuerst',
      biggestProductionFirst: 'Größte Produktionsleistung',
      mostLikes: 'Meiste Likes'
    },
    lock: {
      lockTypes: {
        Project: 'Projekt',
        EG: 'Energiegemeinschaft',
        PPA: 'PPA'
      },
      acquireLock: 'Verbindung fixieren',
      releaseLock: 'Verbindung freigeben',
      lockAlt: 'Verbindungssperre'
    },
    like: 'eFriend liken',
    unlike: 'Nicht mehr liken',
    zeroFriends: 'Bisher noch keine Likes dieses Monat',
    gridFeeDiscountApplies: 'Inkl. @price Cent Netzkostenersparnis',
    priority: 'Priorität',
    specialPrice: 'Sonderpreis',
    actions: 'Aktionen',
    capacity: 'Leistung',
    used: 'verwendet',
    energyLine: 'Energyline',
    nextPriceFrom: 'Ab @date',
  },
  history: {
    menuTitle: 'Verlauf',
    pricesUpdating: 'Preise werden aktualisiert…',
    myEnergyMix: 'Mein Energiemix',
    consumption: 'Verbrauch',
    production: 'Produktion',
    myProducers: 'Meine Produzenten',
    myConsumers: 'Meine Konsumenten',
    dayPrefix: 'Am',
    monthPrefix: 'Im',
    consumed: 'bezogen:',
    produced: 'eingespeist:',
    delivered: 'geliefert:',
    noPastDayPrice: 'Für vergangene Tage wird zur Zeit keine Preiskurve und kein durchschnittlicher Strompreis dargestellt.',
    avgConsumptionPricePre: 'Der heutige bisherige durchschnittliche Strompreis liegt bei',
    avgProductionPricePre: 'Die heutige Einspeisung wird durchschnittlich um',
    avgProductionPricePost: 'ct/kWh* vergütet.',
    avgProductionPricePreIfAvailable: 'Die heutige Einspeisung wird durchschnittlich um <strong>@priceProduction</strong> ct/kWh* vergütet.',
    avgProductionPricePreIfNotAvailable: 'Die heutige Einspeisung wird zu den Konditionen @tariffName inkl. der verkauften Community-Mengen vergütet.',
    avgProductionPriceLegalHint: 'Hinweis: Die Einspeisevergütung des Tarifs wird als grau strichlierte Linie dargestellt und stellt lediglich eine laufend aktualisierte Preisprognose auf Basis des Referenzmarktwerts für Photovoltaik gemäß § 13 EAG dar.<br />Die finale Einspeisevergütung wird monatlich im Nachhinein berechnet.',
    perKwhSuffix: 'ct/kWh*.',
    tradingDataPre: 'Die nachfolgenden Trading-Daten sind von',
    tradingDataToday: 'heute',
    tradingDataPost: 'Uhr und werden mehrmals täglich aktualisiert.',
    meterlessNote: 'Das Trading basiert auf Prognosewerten.',
    minEnergyNote: 'Produktions- bzw Bezugswerte unter @amount kWh werden aus Gründen der Übersichtlichkeit nicht dargestellt.',
    priceDisclaimer: '* Der angezeigte Strompreis basiert auf einer nicht-geeichten Messung oder einer Prognose. Die tatsächliche Verrechnung kann daher abweichen.',
  },
  availableCubes: {
    title: 'Bitte wählen Sie einen Vertrag aus:'
  },
  home: {
    menuTitle: 'Aktuell',
    consumption: 'Mein Verbrauch',
    production: 'Meine Einspeisung',
    communityConsumption: 'Marktplatz Community',
    balance: 'Bilanz',
    prognosisValue: '(Prognosewert)'
  },
  login: {
    user: 'Benutzername/E-Mail',
    password: 'Passwort',
    submit: 'Anmelden',
    pass: 'Anmelden',
    resetPassword: 'Zurücksetzen'
  },
  newPassword: {
    title: 'Neues Passwort',
    submit: 'Passwort ändern'
  },
  notFound: {
    title: 'Seite nicht gefunden',
    prefix: 'Gehe',
    backToHome: 'zurück zur Startseite',
    middle: 'oder',
    contact: 'kontaktiere uns',
  },
  confirmEmail: {
    title: 'Bitte bestätige deine E-Mail-Adresse.',
    text: 'Wir haben eine E-Mail an dich geschickt. Bitte klicke auf den Link in der E-Mail um deine Registrierung abzuschließen.',
  },
  settings: {
    menuTitle: 'Einstellungen',
    watchFaceTitle: 'Watch-Anzeige',
    sellingPriceTitle: 'Basisverkaufspreis',
    brightnessOutsideTime: 'Helligkeit außerhalb der Zeit',
    watchFaceError: 'Es gab einen Fehler bei der Umstellung.',
    watchFaceClassic: 'Bilanz',
    watchFaceV2: 'Consume',
    watchFaceManual: 'Manuell',
    currentNetPrice: 'Aktuell (netto)',
    netSuffix: '(netto)',
    producerInfo1: 'Der Basisverkaufspreis ist der Preis, den du für alle neuen Verbindungen anbietest. Zusätzlich kannst du auf der Seite "eFriends" Sonderpreise für bestehende Verbindungen einstellen.',
    producerInfo2: 'Achte stets darauf, dass Sonderpreise niedriger sind als der Basisverkaufspreis.',
    producerInfo3: 'Wenn du individuelle Sonderpreise löscht, gilt wieder der Basisverkaufspreis.',
    producerInfo4: 'Trennt sich ein eFriends von dir und verbindet sich wieder, gilt der bis dahin gültige Sonderpreis.',
    producerInfo5: 'Jede Preis-Änderung (egal ob Basisverkaufspreis oder Sonderpreis) wird nach Speichern sofort deinen verbundenen eFriends per Push-Benachrichtigung zugesendet.',
    noInvoices: 'Es sind noch keine Rechnungen vorhanden.',
    loadingInvoices: 'Rechnungen werden geladen...',
    noDownloads: 'Es sind keine Downloads vorhanden.',
    uploadImage: 'Bild hochladen',
    imageLabel: 'Bild',
    imageAlt: 'Bild von @name',
    publicCubeDescriptionLabel: 'Beschreibung',
    publicContactPhoneNumber: '+43 664 123 122',
    publicContactPhoneNumberDisclaimer:
      'Deine Telefonnummer wird nur dann für andere eFriends freigegeben, wenn du die WhatsApp-Kommunikation aktivierst. Du kannst diese Einstellung jederzeit ändern oder wieder deaktivieren.',
    publicContactPhoneNumberDescription:
      'Andere eFriends können dich über WhatsApp kontaktieren und sich mit dir über Strompreise, Anlagen, Energy Sharing und Energiegemeinschaften austauschen.',
    publicContactPhoneNumberLabel: 'Öffentlich sichtbare Whatsapp-Telefonnummer',
    watchFace: `Wähle aus zwei Ansichten für deine eFriends Watch zu Hause. Details zu
      den Ansichten findest du`,
    watchFaceBrightness: 'Helligkeit',
    watchFaceTimer: 'Zeitgesteuerte Helligkeit',
    watchFaceAuto: 'Sonnenstandsabhängig',
    highContrast: 'Darstellung mit hohem Kontrast',
    contractData: {
      serviceProvider: 'Mein Service-Anbieter Strom',
      address: 'Adresse',
      support: 'Support',
      contract: 'Vertrag',
      nickname: 'Nickname',
      connectTariff: 'Connect Tarif',
      meterPointId: 'Zählpunktnummer',
      contractNumber: 'Vertragsnummer',
      name: 'Name',
      email: 'E-Mail',
      phone: 'Telefon',
      referralCodeTitle: 'Dein eFriends Code zum Weiterleiten',
      referralQrAria: 'QRCode zum Teilen mit anderen eFriends',
      whatsappInvite: 'Freunde einladen via WhatsApp',
      whatsappMessage: 'Hey 👋, ich bin bei eFriends – da teilen wir Strom direkt miteinander ⚡️. Wenn du mitmachst, bekommst du 30 € Startbonus (und ich auch 🎉).\nHier gehts zur Anmeldung: ',
      referralDescPre: 'Teile diesen',
      referralDescLink: 'Link',
      referralDescPost: 'Dein neuer eFriend erhält 30€ Rabatt auf die eFriends WATCH+ und du bekommst eine Gutschrift im Wert von 30€. Umso mehr eFriends du überzeugst, umso eher wirst du "Hero of the Energiewende" 2025.',
      deliveryAddress: 'Lieferadresse',
      zipCity: 'Plz, Ort',
      country: 'Land',
      billingAddress: 'Rechnungsadresse',
      sameBillingAddress: 'Die Rechnungsadresse entspricht deiner Lieferadresse.',
    },
  },
  becomeEfriend: {
    menuTitle: 'Jetzt wechseln'
  },
  tour: {
    menuTitle: 'Tour beginnen',
    afterTourTitle: 'Geschafft!',
    afterTourDescription: 'Du bist jetzt eFriend und kannst dich auf der eFriends Plattform umsehen.',
    afterTourPopupTitle: 'Zu Entdecken gibt es außerdem noch...',
    afterTourStep0: 'Mit Filter und Liste kannst du andere eFriends finden',
    afterTourStep1: 'Bei den Symbolen unten findest du weitere Funktionen',
    afterTourStepSwitch: 'Wenn du schon überzeugt bist, kannst du bei neuer eFriend zu uns wechseln',
    afterTourStep2Prefix: 'Mehr Antworten findest du in den',
    afterTourFaqLink: 'FAQs',
    afterTourContinue: 'Weiter ausprobieren',
  },
  shop: {
    menuTitle: 'Shop',
    title: 'eFriends Shop',
    bookingSuccess: 'Buchung erfolgreich.',
    activePackages: 'Aktive Pakete',
    availablePackages: 'Verfügbare Pakete / Produkte',
    recentOrders: 'Kürzliche Bestellungen',
    pricesNote: 'Alle Preise inklusive Umsatzsteuer.',
    paymentNote: 'Die Bezahlung der Zusatzpakete erfolgt über deine nächste Monatsrechnung.',
    legalPrefix: 'Es gelten die',
    legalMiddle: ', sowie die',
    termsLabel: 'AGB',
    privacyLabel: 'Datenschutzerklärung',
    month: 'Monat',
    bookingError: 'Es gab einen Fehler bei der Buchung.',
    consentRequired: 'Du musst die AGB akzeptieren um zu buchen.',
    consentPrefix: 'Ich akzeptiere die',
    consentTerms: 'AGBs',
    consentMiddle: ', sowie die',
    consentPrivacy: 'Datenschutzerklärung',
    consentSuffix: 'der eFriends GmbH',
    sepaConsent: 'Ich stimme der Nutzung meines bestehenden SEPA-Mandats zur einmaligen Nutzung als Zahlungsmethode für diese Bestellung zu.',
    bookableLater: 'Du kannst den Artikel nach Vertragsbeginn buchen',
    cancellationNotice: 'Kündigungsfrist:',
    containedArticles: 'Enthaltene Artikel:',
    bookableLaterShort: 'Nach Vertragsbeginn buchbar',
    detailsAriaLabel: 'Details anzeigen',
    orderButton: 'Kostenpflichtig Bestellen',
    bookButton: 'Kostenpflichtig Buchen',
    bookedOn: 'Gebucht am:',
    validLabel: 'Gültig',
    validFromPrefix: 'ab',
    cancelledOn: 'Gekündigt zum:',
    cancelPackage: 'Paket kündigen',
    cancelAlertPrefix: 'Du musst erst',
    cancelAlertLinkText: '@count @unit entfernen',
    cancelAlertSuffix: ', bevor dieses Paket gekündigt werden kann.',
  },
  units: {
    watt: 'W',
    kilowatt: 'kW',
    wattPeak: 'Wp',
    kilowattPeak: 'kWp',
    wattHours: 'Wh',
    kilowattHours: 'kWh',
    megawattHours: 'MWh',
    cent: 'Cent',
    centPerKilowatt: 'Cent / kWh'
  },
  productionTypes: {
    pv: 'Photovoltaikanlage',
    wind: 'Kleinwindradanlage',
    water: 'Kleinwasserkraftwerk',
    kleinwindkraft: 'Kleinwindradanlage',
    kleinwasserkraft: 'Kleinwasserkraftwerk',
    biogas: 'Biogasanlage',
    battery: 'Stromspeicher mit PV',
    eeg: 'EEG',
    p2p: 'P2P',
    none: 'Bezugskunde'
  } as Record<string, string>,
  energyTypes: {
    pv: 'Photovoltaik',
    wind: 'Windkraft',
    water: 'Wasserkraft',
    biogas: 'Biogas',
    eeg: 'EEG',
    battery: 'Stromspeicher mit PV',
    unknown: 'Unbekannt',
    supplier: 'eFriends Energy'
  },
  map: {
    toggleSidebarText: 'Liste',
    xOpenConnections: '@num freie EnergyLines'
  },
  cubeFilter: {
    activeFilters: 'Aktive Filter: ',
    filter: 'Filtern',
    onlyProducers: 'Nur Produzenten',
    onlyProducersWithCapacity: 'Von denen ich beziehen kann',
    onlyProducersWithGridFeeDiscount: 'Mit Netzkostenersparnis',
    onlyMyFriends: 'Nur meine eFriends',
    donationRecipients: 'Strom spenden ❤️',
    energyType: 'Energieerzeugung',
    allEnergyTypes: 'Alle Arten',
    price: 'Preis',
    from: 'Von:',
    to: 'Bis:',
    priceUnit: 'ct/kWh',
    reset: 'Zurücksetzen',
    results: 'Ergebnisse',
    search: 'Suche',
    searchPlaceholder: 'Nach Namen suchen',
  },
  price: {
    noSupplyPossible: 'Kein Preis verfügbar',
  },
  myData: {
    menuTitle: 'Meine Daten'
  },
  downloads: {
    menuTitle: 'Downloads'
  },
  portfolio: {
    forecastedConsumption: 'Prognostizierter Verbrauch',
    pricePerKwh: 'Preis/kWh',
    community: 'Community',
    residualPower: 'Reststrom',
    estimatedEnergyPrice: 'Geschätzter Energiepreis',
  },
  experts: {
    menuTitle: 'Expertenmodus',
    copied: 'Kopiert',
    meterMeasurementLabel: 'Aktuelle Zählermessung und Funkqualität',
    meterSetupLink: 'eFriends Meter Setup',
    restartLabel: 'Cube neu starten',
    restartLink: 'Restart',
    restartRunning: 'Restart läuft (dauert rund 3 Minuten)...',
    restartNote: 'Falls der Cube Verbindungen verliert oder andere Störungsanzeichen vorhanden sind, kann der Cube über diesen Link neu gestartet werden. Ein Restart dauert rund 3 Minuten.',
    hostnameNote: 'Zur Einbindung deiner eFriends Technik in Fremddiensten kann dieser Hostname verwendet werden:',
    remoteHost: 'Remote Host:',
    localIp: 'Lokale IP-Adresse:',
    noExpertSettings: 'Keine Experten-Einstellungen verfügbar.',
  },
  invoices: {
    menuTitle: 'Rechnungen',
    yearFilter: 'Jahr',
  },
  aboutApp: {
    menuTitle: 'Über die App'
  },
  devices: {
    menuTitle: 'Geräte',
    scan: 'Neue Steckdosen suchen',
    confirmDelete: 'Möchtest du dieses Gerät wirklich löschen?',
    modes: {
      measure: 'Verbrauch messen',
      manual: 'Manuell',
      optimized: 'Zeitschaltuhr'
    },
    name: 'Name',
    description: 'Beschreibung',
    consumption: 'Verbrauch',
    consumptionOnDay: 'Verbrauch am @date',
    consumptionInMonth: 'Verbrauch im @date',
    timer: 'Zeitschaltuhr',
    activateTimer: 'Zeitschaltuhr aktivieren',
    manual: 'Manuell',
    settings: 'Einstellungen',
    applianceType: 'Geräteart',
    isOn: 'ein',
    isOff: 'aus',
    turnOn: 'Gerät einschalten',
    turnOff: 'Gerät ausschalten',
    deleteDevice: 'Dieses Gerät löschen',
    isNotReachable: 'nicht erreichbar',
    deviceIsNotReachable: 'Wir können <em>@device</em> aktuell nicht erreichen.',
    applianceTypeHelpText:
      'Jede Geräteart wird in der Verbrauchsgrafik zur besseren Übersichtlichkeit in einer eigenen Farbe gezeigt.',
    currentConsumption: 'Aktueller Verbrauch: @value',
    currentTemperature: 'Temperatur: @value',
    allDevices: 'Alle Geräte',
    overviewDescription: 'In der eFriends App kannst du unterschiedliche Geräte mit vielfältigen Funktionen einbinden.',
    manualControlTitle: 'Manuelle Steuerung',
    manualControlDescription: 'Zum Monitoren des Stromverbrauchs und manuellen Steuerung mittels Zeitschaltuhr von myStrom Steckdosen.',
    smartHomeTitle: 'Smart Home',
    smartHomeDescription: 'Überschüssigen Strom von deiner Anlage oder Strom aus der Community effizient nutzen um deine Geräte smart zu steuern.',
    activateButton: 'Jetzt aktivieren',
    deviceManagerTitle: 'Geräte-Manager',
    deviceManagerDescription: 'Mit dieser Funktion kannst du den Stromverbrauch von allen herkömmlichen Haushaltsgeräten steuern.',
    deviceManagerInstallPrefix: 'Du nimmst eine',
    deviceManagerInstallSuffix: 'und steckst diese zwischen dem Haushaltsgerät und der Wandsteckdose.',
    deviceManagerDetailsTitle: 'Details zur Einbindung:',
    deviceManagerManualLink: 'Bedienungsanleitung manuelle Steuerung',
    deviceManagerAppLink: 'Einbindung myStrom WiFi in App',
    deviceManagerSummary: 'Schon wird der Stromverbrauch eines deiner angeschlossenen Geräte angezeigt und unter Zeitschaltuhr kannst du dieses Gerät steuern lassen. Unter "Verbrauch" kannst du dir ansehen, welche deiner Geräte, wann wieviel Strom verbraucht haben und so deinen Stromverbrauch optimieren.',
    orderSocket: 'Steckdose bestellen',
    socketUnavailable: 'Derzeit nicht lieferbar',
    noDevicesAdded: 'Du hast noch keine Geräte hinzugefügt.',
    noDeviceData: 'Für diesen Zeitraum sind keine Gerätedaten verfügbar.',
    errorTimeSlotsInvalid:
      'Ungültige Zeitangabe. Die Endzeit muss vor der nächsten Startzeit liegen.',
    timerActiveCantSwitch: 'Für dieses Gerät ist die Zeitschaltuhr ist aktiv.',
    manualSwitchingWhileTimerActiveMessage:
      'Wenn du die myStrom Steckdose händisch ein- bzw ausschaltest, während die Zeitschaltuhr aktiv ist, wird die Zeitschaltuhr deaktiviert.',
    defaultOnState: 'Zustand wenn nicht erreichbar',
    efriendsDevices: 'Manuell',
    info: 'Info',
    wallbox: 'Wallbox aktivieren',
    excessPlug: 'Überschuss Steckdose'
  },
  meterDevices: {
    menuTitle: 'Zähler & Wechselrichter',
    scan: 'Konfiguration starten'
  },
  notifications: {
    menuTitle: 'Benachrichtigungen',
    title: 'Benachrichtigungen',
    markAsRead: 'Als gelesen markieren',
    markAllAsRead: 'Alle als gelesen markieren',
    showMore: 'mehr',
    toggleUnread: 'Gelesene anzeigen',
    welcomeNotification:
      'Wunderbar, du erhältst ab sofort Benachrichtigungen für deine eFriends App!'
  },
  apiManager: {
    menuTitle: 'API Manager',
    title: 'API Manager',
    apiKeysTitle: 'API Keys',
    noKeysText: 'Du hast noch keine API Keys erstellt. API Keys werden benötigt, wenn du unsere eFriends Schnittstellen sicher für andere Dienste freischalten willst.',
    learnMorePrefix: 'Hier erfährst du mehr:',
    learnMoreLink: 'Anleitung',
    addKeyButton: 'neuen API Key hinzufügen',
    createKeyText: 'Du kannst einen eigenen Schlüssel (mind. 16 Zeichen lang) erstellen oder die App kann dir einen neuen Key generieren indem du auf das Schlüssel-Symbol klickst.',
    descriptionPlaceholder: 'Optionale Beschreibung',
    newKeyPlaceholder: 'Neuer Key',
    generateKeyAriaLabel: 'Schlüssel generieren',
  },
  help: {
    menuTitle: 'Hilfe',
    title: 'Hilfe'
  },
  basePricePopup: {
    title: 'Verkaufspreis deines Stroms',
    text: 'Du hast noch keinen Verkaufspreis für deinen Strom bestimmt.',
    button: 'Verkaufspreis festlegen'
  },
  days: {
    all: 'Alle Tage',
    monday: 'Montag',
    tuesday: 'Dienstag',
    wednesday: 'Mittwoch',
    thursday: 'Donnerstag',
    friday: 'Freitag',
    saturday: 'Samstag',
    sunday: 'Sonntag'
  },
  timer: {
    addSlot: 'hinzufügen',
    removeSlot: 'entfernen'
  },
  timespans: {
    day: 'Tag',
    month: 'Monat'
  },
  newsletter: {
    promoTitle: 'Sichere dir 10% Willkommensrabatt.',
    promoSubtitle: 'Zum Newsletter anmelden.',
    confirm: 'Ja',
    decline: 'Nein',
    notFound: 'Nicht dabei was du suchst?',
    wantMore: 'Ja, ich will mehr über eFriends erfahren.',
    subscribe: 'Newsletter bestellen',
    thankYou: 'Danke, wir halten dich auf dem Laufenden!',
  },
  chooseProvider: {
    tariffLink: 'Tarifdetails',
    signup: 'Jetzt wechseln',

    home: {
      title: 'Aktueller Verbrauch in Echtzeit',
      text: 'Wenn du deinen aktuellen Verbrauch in Echtzeit sehen möchtest, werde Kunde bei einem der Stromanbieter der eFriends Plattform!'
    },
    history: {
      title: 'Statistiken zum Verbrauch',
      text: 'Kunden der eFriends Plattform können jederzeit übersichtliche Statistiken ihres Verbrauchs abrufen. Werde Kunde bei einem der Stromanbieter der eFriends Plattform!'
    },
    devices: {
      title: 'Geräte Manager',
      text: 'Wenn du deine intelligenten Geräte über die eFriends Map steuern möchtest, werde Kunde bei einem der Stromanbieter der eFriends Plattform!'
    },
    map: {
      title: 'Strom beziehen in Echtzeit',
      text: 'Um von anderen eFriends Strom beziehen zu können, werde Kunden bei einem der Stromanbieter der eFriends Plattform!'
    }
  }
}

export function getString(key: string): string | null {
  return key.split('.').reduce((value: any, arg) => {
    return !value ? null : value[arg] || null
  }, strings)
}

/**
 * Merges the backend provided production types with the statically defined ones in the frontend.
 */
export function setProductionTypes(productionTypes: ProductionTypes): void {
  // originally types from the backend used to overwrite the frontend types, but this makes no sense
  // So we merge in the backend types that are not yet defined in the frontend
  strings.productionTypes = {
    ...productionTypes,
    ... strings.productionTypes,
  }
}

export default strings
